/**
 * @class AccordionItem
 * @extends HTMLElement
 *
 * Represents a single accordion item.
 */

class AccordionItem extends HTMLElement {
  constructor() {
    super();
  }

  selectors = {
    toggle: "[data-accordion-toggle]",
    content: "[data-accordion-content]"
  };

  modifiers = {
    open: "accordion-item--open"
  };

  connectedCallback() {
    if (this._iaAccordionBound) {
      return;
    }

    this.toggleElement = this.querySelector(this.selectors.toggle);
    this.contentElement = this.querySelector(this.selectors.content);

    if (!this.toggleElement || !this.contentElement) {
      return;
    }

    this._iaAccordionToggleClick = () => {
      this.dispatchEvent(
        new CustomEvent("accordion:toggle", {
          bubbles: true,
          detail: {
            item: this
          }
        })
      );

      this.toggle();
    };

    this.toggleElement.addEventListener("click", this._iaAccordionToggleClick);
    this._iaAccordionBound = true;

    if (!this.isOpen) {
      this._iaAccordionHasRendered = true;
    }

    if (this.isOpen) {
      this._updateState();
    }
  }

  disconnectedCallback() {
    if (!this._iaAccordionBound) {
      return;
    }
    this.toggleElement?.removeEventListener(
      "click",
      this._iaAccordionToggleClick
    );
    this._disconnectVisibilityObserver();
    clearTimeout(this._iaAccordionReleaseTimer);
    this._iaAccordionBound = false;
  }

  get isOpen() {
    return this.hasAttribute("open");
  }

  set isOpen(value) {
    if (value) {
      this.setAttribute("open", "");
    } else {
      this.removeAttribute("open");
    }

    this._updateState();
  }

  open() {
    this.isOpen = true;
  }

  close() {
    this.isOpen = false;
  }

  toggle() {
    this.isOpen = !this.isOpen;
  }

  _updateState() {
    if (this.isOpen) {
      this.classList.add(this.modifiers.open);
      this.toggleElement.setAttribute("aria-expanded", "true");
      this._expandContent();
    } else {
      this.classList.remove(this.modifiers.open);
      this.toggleElement.setAttribute("aria-expanded", "false");
      this._disconnectVisibilityObserver();
      this._collapseContent();
    }
  }

  // Animate from the rendered height, since an open item rests at max-height: none.
  _collapseContent() {
    const content = this.contentElement;

    if (content.style.maxHeight === "none") {
      content.style.maxHeight = `${content.scrollHeight}px`;
      void content.offsetHeight;
    }

    content.style.maxHeight = "0";
  }

  // Once the open animation has run, release the fixed height so late-loading
  // content (images, embeds, reviews) is never clipped. A timer (not
  // transitionend) so it also settles when transitions are off or throttled.
  _releaseHeightAfterTransition() {
    const content = this.contentElement;
    const duration = parseFloat(getComputedStyle(content).transitionDuration) || 0;

    clearTimeout(this._iaAccordionReleaseTimer);
    this._iaAccordionReleaseTimer = setTimeout(() => {
      if (this.isOpen) {
        content.style.maxHeight = "none";
      }
    }, duration * 1000 + 50);
  }

  _expandContent() {
    const height = this.contentElement.scrollHeight;

    if (!this._iaAccordionHasRendered) {
      this._iaAccordionHasRendered = true;
      this._disconnectVisibilityObserver();
      this.contentElement.style.maxHeight = "none";
      return;
    }

    if (height > 0) {
      this._disconnectVisibilityObserver();
      this.contentElement.style.maxHeight = `${height}px`;
      this._releaseHeightAfterTransition();
      return;
    }

    // scrollHeight is 0 because the item is hidden when measured (e.g. behind a
    // display:none AB-test wrapper). Recompute once it becomes visible so the
    // open state is truthful instead of "open icon, collapsed body".
    if (!this._visibilityObserver) {
      this._visibilityObserver = new ResizeObserver(() => {
        if (this.isOpen && this.contentElement.scrollHeight > 0) {
          this.contentElement.style.maxHeight = `${this.contentElement.scrollHeight}px`;
          this._disconnectVisibilityObserver();
        }
      });
      this._visibilityObserver.observe(this.contentElement);
    }
  }

  _disconnectVisibilityObserver() {
    this._visibilityObserver?.disconnect();
    this._visibilityObserver = null;
  }
}

if (!customElements.get("accordion-item")) {
  customElements.define("accordion-item", AccordionItem);
}

/**
 * Global click handler to close accordion items from the same group.
 */
document.addEventListener("DOMContentLoaded", () => {
  function AccordionController() {
    document.addEventListener("accordion:toggle", (event) => {
      const toggledItem = event.detail?.item;

      if (!toggledItem || !toggledItem.getAttribute("group")) {
        return;
      }

      const groupElements = document.querySelectorAll(`accordion-item[group="${toggledItem.getAttribute("group")}"]`);

      groupElements.forEach((item) => {
        if (!item.isSameNode(toggledItem) && typeof item.close === "function") {
          item.close();
        }
      });
    });
  }

  AccordionController();
});

/**
 * Global click handler for anchor elements.
 */
document.addEventListener ("DOMContentLoaded", () => {
  document.addEventListener("click", (event) => {
    const anchorElement = event.target.closest("a[href^='#']");

    if (!anchorElement) {
      return;
    }

    const targetUrl = new URL(anchorElement.href);

    if (targetUrl.hash === "") {
      return;
    }

    const targetElement = document.querySelector(targetUrl.hash);

    if (!targetElement) {
      return;
    }

    event.preventDefault();

    targetElement.scrollIntoView({
      behavior: "smooth"
    });
  });
});

/**
 * @class BoundedVisibility
 * @extends HTMLElement
 *
 * A custom element adding class when it is scrolled into view between two boundary elements.
 */
class BoundedVisibility extends HTMLElement {
  constructor() {
    super();
  }

  get _modifier() {
    return this.getAttribute("visible-class-name") || "is-visible";
  }

  get _fromElement() {
    return document.querySelector(this.getAttribute("from"));
  }

  get _toElement() {
    return document.querySelector(this.getAttribute("to"));
  }

  connectedCallback() {
    if (!this._fromElement || !this._toElement) {
      return;
    }

    this._observer = new IntersectionObserver(this._onIntersection.bind(this), {
      root: null,
      threshold: 0,
    });

    this._observer.observe(this._fromElement);
    this._observer.observe(this._toElement);
  }

  disconnectedCallback() {
    if (this._observer) {
      this._observer.disconnect();
    }
  }

  _onIntersection(entries) {
    entries.forEach((entry) => {
      if (entry.target === this._fromElement) {
        this._isFromElementScrolledPast = entry.boundingClientRect.bottom < 0;
      }

      if (entry.target === this._toElement) {
        this._isToElementScrolledPast = entry.boundingClientRect.bottom < 0;
      }
    });

    if (this._isFromElementScrolledPast && !this._isToElementScrolledPast) {
      this.classList.add(this._modifier);
    } else {
      this.classList.remove(this._modifier);
    }
  }
}

if (!customElements.get("bounded-visibility")) {
  customElements.define("bounded-visibility", BoundedVisibility);
}

/**
 * @class ConditionalVisibility
 * @extends HTMLElement
 *
 * A custom element adding class when an observed element is not in view.
 */
class ConditionalVisibility extends BoundedVisibility {
  constructor() {
    super();
  }

  get _observeElement() {
    return document.querySelector(this.getAttribute("observe"));
  }

  connectedCallback() {
    if (!this._observeElement) {
      return;
    }

    this._observer = new IntersectionObserver(this._onIntersection.bind(this), {
      root: null,
      threshold: 0,
    });

    this._observer.observe(this._observeElement);

    if (this._fromElement) {
      this._observer.observe(this._fromElement);
    }

    if (this._toElement) {
      this._observer.observe(this._toElement);
    }
  }

  _onIntersection(entries) {
    entries.forEach((entry) => {
      if (this._fromElement) {
        if (entry.target === this._fromElement) {
          this._isFromElementScrolledPast = entry.boundingClientRect.bottom < 0;
        }
      }

      if (entry.target === this._observeElement) {
        this._isObservableElementVisible = entry.isIntersecting;
      }

      if (this._toElement) {
        if (entry.target === this._toElement) {
          this._isToElementVisible = entry.isIntersecting;
        }
      }
    });

    const fromCondition = this._fromElement ? this._isFromElementScrolledPast : true;
    const toCondition = this._toElement ? !this._isToElementVisible : true;

    if (fromCondition && !this._isObservableElementVisible && toCondition) {
      this.classList.add(this._modifier);
    } else {
      this.classList.remove(this._modifier);
    }
  }
}

if (!customElements.get("conditional-visibility")) {
  customElements.define("conditional-visibility", ConditionalVisibility);
}

/**
 * @class ReadMore
 * @extends HTMLElement
 *
 * A custom read more toggleable element.
 */
class ViewMore extends HTMLElement {
  constructor() {
    super();

    this._onToggleListener = this._onToggle.bind(this);
    this._onKeyDownListener = this._onKeyDown.bind(this);
  }

  selectors = {
    toggle: "[data-view-more-toggle]",
    content: "[data-view-more-content]",
    contentPreview: "[data-view-more-content-preview]"
  };

  connectedCallback() {
    this._toggleElement = this.querySelector(this.selectors.toggle);
    this._contentElement = this.querySelector(this.selectors.content);
    this._contentPreviewElement = this.querySelector(this.selectors.contentPreview);

    if (!this._toggleElement || !this._contentElement || !this._contentPreviewElement) {
      return;
    }

    this._toggleElement.addEventListener("click", this._onToggleListener);
    this._toggleElement.addEventListener("keydown", this._onKeyDownListener);
  }

  disconnectedCallback() {
    this._toggleElement.removeEventListener("click", this._onToggleListener);
    this._toggleElement.removeEventListener("keydown", this._onKeyDownListener);
  }

  _onToggle() {
    const isExpanded = this._contentElement.hidden === false;

    this._contentElement.hidden = isExpanded;
    this._contentPreviewElement.classList.toggle("hidden", !isExpanded);
    this._toggleElement.textContent = isExpanded ? this.getAttribute("view-more-text") : this.getAttribute("view-less-text");
    this._toggleElement.setAttribute("aria-expanded", (!isExpanded).toString());
  }

  _onKeyDown(event) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      this._onToggle();
    }
  }
}

if (!customElements.get("view-more")) {
  customElements.define("view-more", ViewMore);
}

/**
 * @class CustomTabs
 * @extends HTMLElement
 */
class CustomTabs extends HTMLElement {
  constructor() {
    super();
    this.container = this;
  }

  get selectors() {
    return {
      control: "[data-tab-control]",
      content: "[data-tab-content]",
    };
  }

  get attributes() {
    return {
      index: "data-index"
    };
  }

  get modifiers() {
    return {
      active: "active"
    };
  }

  connectedCallback() {
    this.initTabs();
  }

  get _controls() {
    return [...this.querySelectorAll(this.selectors.control)];
  }

  initTabs() {
    this.addEventListener("click", (event) => {
      const control = event.target.closest(this.selectors.control);
      if (!control || !this.contains(control)) return;

      this._activate(control);
    });

    this.addEventListener("keydown", (event) => {
      const control = event.target.closest(this.selectors.control);
      if (!control || !this.contains(control)) return;

      const controls = this._controls;
      const current = controls.indexOf(control);
      const last = controls.length - 1;
      const targets = {
        ArrowRight: current === last ? 0 : current + 1,
        ArrowLeft: current === 0 ? last : current - 1,
        Home: 0,
        End: last
      };

      if (!(event.key in targets)) return;

      event.preventDefault();
      const next = controls[targets[event.key]];
      next.focus();
      this._activate(next);
    });
  }

  _activate(control) {
    if (control.classList.contains(this.modifiers.active)) return;

    const controls = this._controls;
    const previousIndex = controls.findIndex((el) => el.classList.contains(this.modifiers.active));
    const nextIndex = controls.indexOf(control);
    const index = control.getAttribute(this.attributes.index);
    const content = this.querySelector(`${this.selectors.content}[${this.attributes.index}="${index}"]`);

    controls.forEach((el) => {
      const isActive = el === control;
      el.classList.toggle(this.modifiers.active, isActive);
      el.setAttribute("aria-selected", String(isActive));
      el.tabIndex = isActive ? 0 : -1;
    });

    this.querySelectorAll(this.selectors.content).forEach((el) => el.classList.toggle(this.modifiers.active, el === content));

    const list = control.closest('[role="tablist"]');
    if (list) {
      list.dataset.direction = nextIndex > previousIndex ? "forward" : "backward";
      list.dataset.activeIndex = String(nextIndex + 1);
    }

    this.dataset.interacted = "";
  }
}

if (!customElements.get("custom-tabs")) {
  customElements.define("custom-tabs", CustomTabs);
}

/**
 * @class Quick Add Form
 * @extends HTMLElement
 */

if (!customElements.get("od-quick-add-form")) {
  class QuickAddForm extends HTMLFormElement {
    selectors = {
      cartDrawer: "cart-drawer"
    };

    constructor() {
      super();

      this._onFormSubmitted = this._onFormSubmit.bind(this);
    }

    connectedCallback() {
      this.cartDrawerElement = document.querySelector(this.selectors.cartDrawer);

      this.addEventListener("submit", this._onFormSubmitted);
    }

    disconnectedCallback() {
      this.removeEventListener("submit", this._onFormSubmitted);
    }

    async _onFormSubmit(event) {
      event.preventDefault();

      const formData = new FormData(this);

      if (this.cartDrawerElement) {
        formData.append(
          "sections",
          this.cartDrawerElement.getSectionsToRender().map((section) => section.id)
        );

        formData.append("sections_url", window.location.pathname);
        this.cartDrawerElement.setActiveElement(document.activeElement);
      }

      const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: "POST",
        body: formData,
        headers: {
          "X-Requested-With": "XMLHttpRequest"
        }
      });

      const responseJson = await response.json();

      if (response.ok) {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: "product-form",
          productVariantId: formData.get("id")
        });

        this._onFormSubmitSuccess(responseJson);
      } else {
        publish(PUB_SUB_EVENTS.cartError, {
          source: "product-form",
          productVariantId: formData.get("id"),
          errors: response.description,
          message: response.message
        });
      }
    }

    _onFormSubmitSuccess(response) {
      this._confirmAdded();

      if (this.cartDrawerElement) {
        this.cartDrawerElement.renderContents(response);
      }
    }

    _confirmAdded() {
      const button = this.querySelector('[type="submit"]');
      if (!button) return;

      clearTimeout(this._addedTimer);
      button.classList.add("is-added");
      this._addedTimer = setTimeout(() => button.classList.remove("is-added"), 1600);
    }
  }

  customElements.define("od-quick-add-form", QuickAddForm, { extends: "form" });
}

/**
 * Afterpay's <square-placement> hardcodes a 20px logo min-height inside its own
 * shadow root, with no CSS variable or ::part hook, which blocks the smaller
 * --logo-badge-width set in base.css. Attach a constructed stylesheet there
 * (survives the component re-rendering its shadow content).
 */
(() => {
  const sheet = new CSSStyleSheet();
  sheet.replaceSync(".afterpay-logo.brand-afterpay, .afterpay-logo.brand-afterpay svg { min-height: 16px; }");

  const applyTo = (placement) => {
    const root = placement.shadowRoot;

    if (!root || root.adoptedStyleSheets.includes(sheet)) {
      return;
    }

    root.adoptedStyleSheets = [...root.adoptedStyleSheets, sheet];
  };

  const applyAll = () => document.querySelectorAll("square-placement").forEach(applyTo);

  customElements.whenDefined("square-placement").then(() => {
    applyAll();

    new MutationObserver(applyAll).observe(document.body, { childList: true, subtree: true });
  });
})();
